import React from 'react';
import { Box, Typography, Button, Container, Skeleton, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useStateValue } from '../../../contexto/store';
import { useInstitutions } from '../../../hooks/useInstitutions';
import { LogoIcon } from '../../tool';

/* SVG ola inferior — color del siguiente bloque (blanco) */
const WaveBottom = () => (
  <Box
    component="svg"
    viewBox="0 0 1440 90"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block', width: '100%', height: { xs: 52, md: 80 } }}
  >
    <path
      d="M0,45 C200,90 400,0 600,45 C800,90 1000,10 1200,50 C1320,75 1390,30 1440,45 L1440,90 L0,90 Z"
      fill="#ffffff"
    />
  </Box>
);

const LandingHero = () => {
  const navigate       = useNavigate();
  const theme          = useTheme();
  const [{ sesionUsuario }] = useStateValue();
  const { institutions, isLoading } = useInstitutions();
  const inst            = institutions?.[0] ?? null;
  const isAuthenticated = sesionUsuario?.autenticado ?? false;
  const p               = theme.palette.primary;

  return (
    <Box id="hero" sx={{ display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

      {/* ─── Fondo degradado de agua ────────────────── */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(to bottom, ${p.main} 0%, ${p.dark} 55%, ${p.darker ?? p.dark} 100%)`,
        zIndex: 0,
      }} />

      {/* Ondas internas de textura agua */}
      <Box
        component="svg"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{ position: 'absolute', bottom: 60, left: 0, width: '100%', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }}
      >
        <path d="M0,160 C360,260 720,60 1080,160 C1260,210 1380,120 1440,160 L1440,320 L0,320 Z" fill="white" />
        <path d="M0,220 C280,160 560,280 840,220 C1120,160 1300,240 1440,220 L1440,320 L0,320 Z" fill="white" opacity="0.6"/>
      </Box>

      {/* ─── Contenido principal ───────────────────── */}
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 14, md: 16 },
          pb: { xs: 4,  md: 8  },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { md: 8 } }}>

          {/* Izquierda: texto */}
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            {/* Badge */}
            <Box sx={{ mb: 3.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: 'white',
                boxShadow: `0 0 0 4px ${alpha('#fff', 0.25)}`,
                animation: 'heroPulse 2.5s ease-in-out infinite',
              }} />
              <Typography variant="overline" sx={{ color: alpha('#fff', 0.85), fontWeight: 700, letterSpacing: 2, fontSize: '0.72rem' }}>
                Sistema de Gestión Hídrica
              </Typography>
            </Box>

            {/* Nombre de la institución */}
            {isLoading ? (
              <>
                <Skeleton variant="text" width="85%" height={72} sx={{ bgcolor: alpha('#fff', 0.15), borderRadius: 2 }} />
                <Skeleton variant="text" width="55%" height={72} sx={{ bgcolor: alpha('#fff', 0.1),  borderRadius: 2, mt: 0.5 }} />
              </>
            ) : (
              <Typography
                component="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2rem', sm: '2.8rem', md: '3.6rem', lg: '4.2rem' },
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  color: '#ffffff',
                  textTransform: 'uppercase',
                }}
              >
                {inst?.name ?? 'AGUA POTABLE'}
              </Typography>
            )}

            {/* About */}
            <Box sx={{ mt: 4, maxWidth: 520 }}>
              {isLoading ? (
                <>
                  <Skeleton variant="text" width="100%" height={22} sx={{ bgcolor: alpha('#fff', 0.12) }} />
                  <Skeleton variant="text" width="80%"  height={22} sx={{ bgcolor: alpha('#fff', 0.10), mt: 0.5 }} />
                </>
              ) : (
                <Typography variant="body1" sx={{ color: alpha('#fff', 0.78), lineHeight: 1.85, fontSize: '1rem' }}>
                  {inst?.about ?? 'Administración responsable y moderna del recurso hídrico para garantizar agua de calidad a cada miembro de la comunidad.'}
                </Typography>
              )}
            </Box>

            {/* CTAs */}
            <Box sx={{ mt: 5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowOutwardIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/auth/login')}
                sx={{
                  px: 3.5, py: 1.5,
                  borderRadius: '10px',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  bgcolor: '#ffffff',
                  color: p.dark,
                  '&:hover': { bgcolor: alpha('#fff', 0.9) },
                  boxShadow: `0 8px 28px ${alpha('#000', 0.2)}`,
                  transition: 'all 0.2s',
                }}
              >
                {isAuthenticated ? 'Ir al Panel' : 'Acceder al Sistema'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                sx={{
                  px: 3.5, py: 1.5,
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  borderColor: alpha('#fff', 0.45),
                  color: '#ffffff',
                  '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) },
                  transition: 'all 0.2s',
                }}
              >
                Conocer más
              </Button>
            </Box>
          </Box>

          {/* Derecha: logo con resplandor */}
          <Box sx={{ flexShrink: 0, width: 420, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Resplandor difuminado detrás */}
              <Box sx={{
                position: 'absolute',
                width: 500, height: 500,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#fff', 0.28)} 0%, ${alpha('#fff', 0.06)} 55%, transparent 75%)`,
                filter: 'blur(20px)',
              }} />
              {/* Logo limpio */}
              {isLoading ? (
                <Skeleton variant="circular" width={340} height={340} sx={{ bgcolor: alpha('#fff', 0.15) }} />
              ) : inst?.logo ? (
                <Box
                  component="img"
                  src={inst.logo}
                  alt={inst.name}
                  sx={{
                    position: 'relative', zIndex: 1,
                    width: 380, height: 380,
                    objectFit: 'contain',
                    filter: `drop-shadow(0 0 30px ${alpha('#fff', 0.55)}) drop-shadow(0 0 10px ${alpha('#fff', 0.3)})`,
                  }}
                />
              ) : (
                <LogoIcon sx={{
                  position: 'relative', zIndex: 1,
                  fontSize: '20rem',
                  color: alpha('#fff', 0.92),
                  filter: `drop-shadow(0 0 30px ${alpha('#fff', 0.55)}) drop-shadow(0 0 10px ${alpha('#fff', 0.3)})`,
                }} />
              )}
            </Box>
          </Box>
        </Box>
      </Container>

      {/* ─── Ola de cierre ─────────────────────────── */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <WaveBottom />
      </Box>

      {/* ─── Strip de contacto ─────────────────────── */}
      <Box sx={{ bgcolor: '#ffffff', borderBottom: `1px solid ${alpha(p.main, 0.1)}` }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', py: { xs: 3, md: 3 }, gap: { xs: 3, md: 0 } }}>
            {[
              { icon: <LocationOnOutlinedIcon sx={{ fontSize: 15 }} />, value: inst?.direction },
              { icon: <LocalPhoneOutlinedIcon sx={{ fontSize: 15 }} />, value: inst?.telephone ? String(inst.telephone) : null },
              { icon: <EmailOutlinedIcon     sx={{ fontSize: 15 }} />, value: inst?.email },
            ]
              .filter((c) => c.value)
              .map((c, i, arr) => (
                <React.Fragment key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: { md: 4 }, flex: { xs: '1 1 auto', md: '1 1 0' } }}>
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>{c.icon}</Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{c.value}</Typography>
                  </Box>
                  {i < arr.length - 1 && (
                    <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(p.main, 0.15), display: { xs: 'none', md: 'block' } }} />
                  )}
                </React.Fragment>
              ))}
          </Box>
        </Container>
      </Box>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes heroPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(255,255,255,0.2); }
          50%       { box-shadow: 0 0 0 9px rgba(255,255,255,0.05); }
        }
      ` }} />
    </Box>
  );
};

export default LandingHero;
